/* eslint-disable indent */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-plusplus */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertAdapter, FullScreenLoading, RegularButton } from 'pg-ui-library';
import Arrow from 'src/assets/arrow-left-black.svg';
import { TableColumn } from 'react-data-table-component';
import { DefaultTable } from 'src/components';
import { monetaryUSDFormat } from 'src/utils/currencyFormat';
import { exportToXLSX } from 'src/utils/xlsxConverter';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { useMutation } from '@tanstack/react-query';
import BenefitsService, { IEnrollmentPost } from 'src/services/benefits.service';

import { useLocation, useNavigate } from 'react-router-dom';

import { DependentsSettings, IBenefitsReview, LifeInsuranceSettings } from 'src/interfaces';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useActions } from 'src/hooks/useActions';
import { SignSubmitModal } from '../sign-submit-modal';

import {
  ArrowBack,
  BeneficiariesContainer,
  Body,
  ColumnHeader,
  Container,
  Footer,
  FooterItemLeft,
  Header,
  HeaderItemLeft,
  NavTextLink,
  PageTitle,
  RelativeContainer,
  TooltipText,
  Wrapper,
} from './styles';

const columns: TableColumn<IBenefitsReview>[] = [
  {
    name: <ColumnHeader>Benefit ID</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.benefitId,
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Plan ID</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.plan,
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Employee ID</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.employeeId,
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Employee Name</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.employeeName,
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Dependent Included</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.dependentIncluded,
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Dependents</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.dependents,
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Beneficiaries</ColumnHeader>,
    cell: (row: IBenefitsReview) => {
      const data = row.beneficiaries.split(',');

      return (
        <Wrapper>
          <BeneficiariesContainer>
            {data.map((bnf, i) => (
              <p
                key={i}
                style={{
                  margin: 0,
                }}
              >
                {bnf}
              </p>
            ))}
          </BeneficiariesContainer>
          <RelativeContainer id="tooltip">
            <TooltipText>
              {data.map(bnf => (
                <>
                  <br />
                  {bnf}
                </>
              ))}
            </TooltipText>
          </RelativeContainer>
        </Wrapper>
      );
    },
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Type</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.type,
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Status</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.status,
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Semi-Monthly</ColumnHeader>,
    selector: (row: IBenefitsReview) => monetaryUSDFormat(row.price),
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Monthly Price</ColumnHeader>,
    selector: (row: IBenefitsReview) => monetaryUSDFormat(row.price * 2),
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Annual Cost</ColumnHeader>,
    selector: (row: IBenefitsReview) => monetaryUSDFormat(row.price * 24),
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Coverage Amount</ColumnHeader>,
    selector: (row: IBenefitsReview) => monetaryUSDFormat(row.coverageAmount),
    sortable: true,
    wrap: true,
  },
];

export const BenefitsQleUeReview = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<IBenefitsReview[]>([]);
  const { clearCart, setQLEFiles } = useActions();
  const [filteredData, setFilteredData] = useState<IBenefitsReview[]>([]);
  const [openSignSubmitModal, setOpenSignSubmitModal] = useState(false);
  const { cart } = useTypedSelector(state => state.benefits.data);
  const { personalInfo } = useTypedSelector(state => state.personalInfo.data);

  const electedData: IBenefitsReview[] =
    cart
      ?.filter(d => d.action === 'ADD')
      .map(b => ({
        benefitId: b.benefitId,
        plan: b.planDescription,
        employeeId: personalInfo?.employeeNickId || '',
        employeeName: `${personalInfo?.firstName} ${personalInfo?.middleName} ${personalInfo?.lastName}`,
        dependentIncluded: b.dependents,
        dependents: (b.settings?.data as DependentsSettings)?.dependents
          ? (b.settings?.data as DependentsSettings).dependents.map(be => be.name).join(',')
          : '',
        beneficiaries:
          b.necessaryToProvideBeneficiariesInformation && (b.settings?.data as LifeInsuranceSettings)?.beneficiaries
            ? (b.settings?.data as LifeInsuranceSettings).beneficiaries
                .map(be => `${be.name} = ${be.percentToPay}%`)
                .join(',')
            : '',
        type: b.type,
        status: 'Elected',
        price: b.price,
        annualCost: b.price * 24,
        coverageAmount: b.coverageAmount ?? 0,
        beneficiaryInfo:
          b.necessaryToProvideBeneficiariesInformation && (b.settings?.data as LifeInsuranceSettings)?.beneficiaries
            ? (b.settings?.data as LifeInsuranceSettings).beneficiaries.map(be => ({
                personId: be.personId,
                percentage: be.percentToPay.toString(),
              }))
            : [],
        dependentsInfo: (b.settings?.data as DependentsSettings)?.dependents
          ? (b.settings?.data as DependentsSettings).dependents.map(be => ({
              personId: be.personId,
            }))
          : [],
      })) || [];

  useEffect(() => {
    const fetchData = async () => {
      setData(electedData);
      setFilteredData(electedData);
    };
    fetchData();
  }, [cart]);

  const navigate = useNavigate();

  const location = useLocation();

  const stateData = location.state as {
    event: 'new_hire' | 'open_enrollment' | 'qle' | 'update_enrollment';
    qle?: {
      files: FileList;
      lifeEvent: string;
      description: string;
      eventDate: string | Date;
    };
  };

  const totalAddedToCart = cart
    ?.filter(benefit => benefit.action === 'ADD')
    ?.map(benefit => benefit.price)
    .reduce((acc, next) => Number(acc) + Number(next), 0) as unknown as 0;

  const downloadReview = () => {
    exportToXLSX(data || [], 'enrolldetails', 'enroll-detail');
  };

  const updateEnrollmentMutation = useMutation((cartData: IEnrollmentPost) =>
    BenefitsService.enrollForUpdateEnrollment(cartData)
  );

  const qleEnrollmentMutation = useMutation((d: FormData) => BenefitsService.enrollForQle(d));

  const [qleData, setQleData] = useLocalStorage('qleData', null);

  const submitQLEEnrollment = async () => {
    try {
      if (!cart) {
        AlertAdapter(t('benefits.qualifyingLifeEvent.emptyCart.line1'), 'error');
        return;
      }

      const removedBenefitIds = cart.filter(benefit => benefit.action === 'REMOVE');
      const addedBenefitIds = cart.filter(benefit => benefit.action === 'ADD');

      const values = {
        ...qleData,
        files: stateData.qle?.files,
        removed: removedBenefitIds,
        added: addedBenefitIds.map(item => ({
          benefitId: item.benefitId,
          ...item.settings?.data,
        })),
        summary: data,
      };
      const fd = new FormData();

      if (values.files) {
        for (let index = 0; index < values.files.length; index++) {
          const file = values.files[index];
          if (file) {
            fd.append('files', file, file?.name || '');
          }
        }
      }

      if (values.removed) {
        for (let index = 0; index < values.removed.length; index++) {
          fd.append(`removed[${index}][benefitId]`, values.removed[index].benefitId);
        }
      }

      if (values.added) {
        fd.append('added', JSON.stringify(values.added));
      }

      if (values.lifeEvent) {
        fd.append('lifeEvent', values.lifeEvent);
      }

      if (values.eventDate) {
        fd.append('eventDate', values.eventDate as string);
      }

      if (values.description) {
        fd.append('description', values.description);
      }

      fd.append('summary', JSON.stringify(data));
      await qleEnrollmentMutation.mutateAsync(fd);

      setQleData(null);
      clearCart();
      setQLEFiles([]);
      navigate('/benefits', { replace: true });
      AlertAdapter(t('benefits.qualifyingLifeEvent.submitSuccess'), 'success');
    } catch (e: any) {
      AlertAdapter(e.response.data.message, 'error');
    }
  };

  const submitUpdateEnrollment = async () => {
    try {
      if (!cart) {
        AlertAdapter(t('benefits.updateEnrollment.emptyCart.line1'), 'error');
        return;
      }

      const addedBenefitIds = cart.filter(benefit => benefit.action === 'ADD');

      await updateEnrollmentMutation.mutateAsync({
        removed: [], // there´s no remove option on new hire
        added: addedBenefitIds.map(item => ({
          benefitId: item.benefitId,
          ...item.settings?.data,
        })),
        summary: data,
      });

      navigate('/benefits', { replace: true });

      AlertAdapter(t('benefits.updateEnrollment.submitSuccess'), 'success');
    } catch (e: any) {
      AlertAdapter(e.response.data.message, 'error');
    }
  };

  const submitCartEnrollment = async () => {
    if (stateData.event === 'qle') {
      submitQLEEnrollment();
    }
    if (stateData.event === 'update_enrollment') {
      submitUpdateEnrollment();
    }
  };

  return (
    <Container>
      {(updateEnrollmentMutation.isLoading || qleEnrollmentMutation.isLoading) && <FullScreenLoading />}
      <Header>
        <HeaderItemLeft>
          <NavTextLink to={-1 as any}>
            <ArrowBack src={Arrow} />
          </NavTextLink>
          <PageTitle>{t('benefits.review.title')}</PageTitle>
        </HeaderItemLeft>

        <div>
          <RegularButton type="button" onClick={downloadReview}>
            {t('benefits.review.downloadReview')}
          </RegularButton>
        </div>
      </Header>
      <Body>
        <DefaultTable columns={columns} data={filteredData} pagination paginationPerPage={30} />
      </Body>
      <Footer>
        <FooterItemLeft>
          <RegularButton
            type="button"
            onClick={() => {
              setOpenSignSubmitModal(prevValue => !prevValue);
            }}
          >
            {t('benefits.review.signAndSubmit')}
          </RegularButton>
        </FooterItemLeft>
        <RegularButton type="button">
          {t('benefits.review.totalOnCart')}
          {monetaryUSDFormat(totalAddedToCart)}
        </RegularButton>
      </Footer>
      <SignSubmitModal
        isOpen={openSignSubmitModal}
        onCloseSuccess={submitCartEnrollment}
        onRequestClose={() => setOpenSignSubmitModal(false)}
      />
    </Container>
  );
};
